import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OAUTH_PROVIDER_CHOICES } from 'src/app/core/constants/provider.constants';
import { User, UserSession } from 'src/app/core/models/user.model';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { ACCESS_TOKEN, ONESIGNAL_SUBSCRIPTION_ID, PUSH_NOTIFICATION_DISABLED_DATE } from 'src/app/core/services/push-notifications/push-notifications.service';
import { StorageService } from 'src/app/core/services/storage/storage.service';
import { UniversalWrapperService } from 'src/app/core/services/universal-wrapper/universal-wrapper.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { AuthenticationApiService, LoginRequest, LoginResponse, OtpRequest, OtpVerificationRequest, SignupRequest } from './authentication-api.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationRepositoryService {
  private router = inject(Router);
  private userService = inject(UserService);
  private authenticationApiService = inject(AuthenticationApiService);
  private universalWrapperService = inject(UniversalWrapperService);
  private environmentService = inject(EnvironmentService);
  private storageService = inject(StorageService);

  // ===== SERVICE STATE MANAGEMENT =====
  private _isLoading = signal<boolean>(false);
  private _currentLoginCredential = signal<string | null>(null);
  private _userLoggedIn = signal<boolean>(false);

  // ===== PUBLIC READONLY SIGNALS =====
  readonly isLoading = this._isLoading.asReadonly();
  readonly currentLoginCredential = this._currentLoginCredential.asReadonly();
  readonly userLoggedIn = this._userLoggedIn.asReadonly();

  // ===== COMPUTED SIGNALS =====

  readonly canAuthenticate = computed(() =>
    !this._isLoading() &&
    this._currentLoginCredential()
  );

  readonly isUserLoggedIn = computed(() => this._userLoggedIn());
  readonly hasLoginCredential = computed(() => !!this._currentLoginCredential());

  // ===== ANGULAR 20 SERVICE AUTO-INITIALIZATION =====
  constructor() {
    this.initializeService();
  }

  // ===== SERVICE INITIALIZATION =====
  async initializeService(): Promise<void> {
    try {
      // Initialize authentication state
      this.autoLogin();
    } catch (error) {
      // Authentication service initialization failed
    }
  }

  // ===== UTILITY METHODS =====
  resetService(): void {
    this._isLoading.set(false);
    this._currentLoginCredential.set(null);
    this._userLoggedIn.set(false);
  }

  getServiceStatus(): {
    isLoading: boolean;
    isUserLoggedIn: boolean;
    hasLoginCredential: boolean;
  } {
    return {
      isLoading: this._isLoading(),
      isUserLoggedIn: this._userLoggedIn(),
      hasLoginCredential: !!this._currentLoginCredential()
    };
  }

  // Public setter methods for signals
  setCurrentLoginCredential(credential: string | null): void {
    this._currentLoginCredential.set(credential);
  }

  // ===== AUTHENTICATION METHODS =====
  /**
   * Send OTP to user
   */
  public async sendOTP(username: string): Promise<void> {

    this._isLoading.set(true);


    const reqObj: OtpRequest = {
      username,
      provider: OAUTH_PROVIDER_CHOICES.consumer,
    };

    await this.authenticationApiService.sendOTP(reqObj);
    this._isLoading.set(false);
  }


  public async sendOTPToMobileNumber(mobile_number: string): Promise<void> {

    this._isLoading.set(true);


    const reqObj = {
      mobile_number,
      provider: OAUTH_PROVIDER_CHOICES.consumer,
    };

    await this.authenticationApiService.sendOTPToMobileNumber(reqObj);
    this._isLoading.set(false);
  }

  /**
   * Verify OTP from user
   */
  public async verifyOTP({ otp }: { otp: string }): Promise<void> {
    this._isLoading.set(true);


    const reqObj: OtpVerificationRequest = {
      verification_token: otp,
      provider: OAUTH_PROVIDER_CHOICES.consumer,
    };

    const response = await this.authenticationApiService.verifyOTP(reqObj);
    this.handleAuthentication(response);

    this._isLoading.set(false);
  }


  public async verifyMobileNumberOTP(reqObj: OtpVerificationRequest): Promise<void> {
    this._isLoading.set(true);

    const response = await this.authenticationApiService.verifyMobileNumberOTP(reqObj);
    this.handleAuthentication(response);

    this._isLoading.set(false);
  }
  /**
   * Login user with credentials
   */
  public async login(reqObj: LoginRequest): Promise<void> {

    this._isLoading.set(true);

    const response = await this.authenticationApiService.login(reqObj);
    if (response?.access_token) {
      this.handleAuthentication(response);
    }

    this._isLoading.set(false);
  }

  /**
   * Sign up new user
   */
  public async signup(reqObj: SignupRequest): Promise<void> {

    this._isLoading.set(true);

    const response = await this.authenticationApiService.signup(reqObj);
    if (response?.access_token) {
      this.handleAuthentication(response);
    }

    this._isLoading.set(false);
  }

  /**
   * Auto-login user from stored credentials
   */
  public autoLogin(): void {
    try {
      const userData: UserSession = this.storageService.retrieve('userData');
      if (!userData || !userData.access_token) {
        this._userLoggedIn.set(false);
        return;
      }

      // Only destructure if userData exists
      const { access_token, refresh_token, expires_in, token_type, user, company } = userData;
      const loadedUser: UserSession = new UserSession(
        access_token, refresh_token, expires_in, token_type, user, company
      );

      if (loadedUser.access_token) {
        this.userService.appUser = loadedUser;
        this._userLoggedIn.set(true);
      }
    } catch (error) {
      // Auto-login failed
      this._userLoggedIn.set(false);
    }
  }

  /**
   * Logout user and clear session
   */
  public logout(): void {
    try {
      this._userLoggedIn.set(false);
      this._isLoading.set(false);

      // Getting subscription id and push notification and accesstoken disable date to restore in localstorage
      const onesignalSubscriptionId = this.storageService.retrieve(ONESIGNAL_SUBSCRIPTION_ID);
      const lastPushNotificationDisabledDate = this.storageService.retrieve(PUSH_NOTIFICATION_DISABLED_DATE);
      const accessToken = this.storageService.retrieve(ACCESS_TOKEN);

      // Clearing localstorage    
      this.storageService.remove('userData');
      this.userService.appUser = new UserSession();

      // Restoring onesignalSubscriptionId
      if (onesignalSubscriptionId) {
        this.storageService.save(ONESIGNAL_SUBSCRIPTION_ID, onesignalSubscriptionId);
      }

      // Restoring lastPushNotificationDisabledDate
      if (lastPushNotificationDisabledDate) {
        this.storageService.save(PUSH_NOTIFICATION_DISABLED_DATE, lastPushNotificationDisabledDate);
      }

      // Restoring accessToken
      if (accessToken) {
        this.storageService.save(ACCESS_TOKEN, accessToken);
      }

      // Reload page
      this.universalWrapperService.getWindow()?.location.reload();
    } catch (error) {
      this.universalWrapperService.getWindow()?.location.reload();
    }
  }

  /**
   * Logout user and navigate to login page
   */
  public async logoutUserAndSendToLogin(): Promise<boolean> {

    try {
      this.storageService.remove('userData');
      this.userService.appUser = new UserSession();
      this._userLoggedIn.set(false);
      return this.router.navigateByUrl('/auth/login-otp');
    } catch (error) {
      return false;
    }
  }

  /**
   * Send reset password OTP
   */
  public async sendResetPasswordOTP(username: string): Promise<void> {

    this._isLoading.set(true);

    const environment = this.environmentService.environment();

    const reqObj: OtpRequest = {
      username,
      provider: OAUTH_PROVIDER_CHOICES.consumer,
      seller_company: environment?.sellerId ?? ''
    };

    await this.authenticationApiService.sendResetPasswordOTP(reqObj);
    this._isLoading.set(false);
  }

  /**
   * Verify reset password OTP
   */
  public async verifyResetPasswordOTP({ otp }: { otp: string }): Promise<void> {

    this._isLoading.set(true);

    const environment = this.environmentService.environment();

    const reqObj: OtpVerificationRequest = {
      verification_token: otp,
      username: '', // This will be set by the API service
      provider: OAUTH_PROVIDER_CHOICES.consumer,
      seller_company: environment?.sellerId ?? ''
    };

    await this.authenticationApiService.verifyResetPasswordOTP(reqObj);
    this._isLoading.set(false);
  }

  /**
   * Reset user password
   */
  public async resetPassword(new_password: string): Promise<void> {

    this._isLoading.set(true);

    const reqObj = {
      new_password
    };

    await this.authenticationApiService.resetPassword(reqObj);
    this._isLoading.set(false);
  }

  /**
   * Reset user session
   */
  public resetUser(): void {
    try {
      this.storageService.remove('userData');
      this.userService.appUser = new UserSession();
      this._userLoggedIn.set(false);
      this._isLoading.set(false);
    } catch (error) {
      // Failed to reset user
    }
  }

  /**
   * Handle successful authentication response
   */
  private handleAuthentication(response: LoginResponse): void {
    try {
      // Only destructure if response exists
      if (!response) {
        return;
      }

      const { access_token, refresh_token, expires_in, token_type, user, company } = response;

      // Convert MeUser to User to maintain compatibility with UserSession
      const userObj = new User(
        user.id,
        user.email,
        user.first_name,
        user.last_name,
        user.username,
        user.gender,
        user.profile_photo,
        user.country,
        user.state,
        user.city,
        user.address_line_1,
        user.address_line_2,
        user.pincode,
        user.phone_number,
        user.is_active,
        user.is_verified,
        user.created_at,
        user.updated_at,
        user.date_of_birth,
        user.verified_at
      );

      const userSession = new UserSession(access_token, refresh_token, expires_in, token_type, userObj, company);
      this.userService.appUser = userSession;
      this.storageService.save('userData', userSession);
      this._userLoggedIn.set(true);
    } catch (error) {
      // Failed to handle authentication
      this._userLoggedIn.set(false);
    }
  }

  public set currentLoginCredentials(currentLoginCredential: string | null) {
    this._currentLoginCredential.set(currentLoginCredential);
  }

  public get currentLoginCredentials(): string | null {
    return this._currentLoginCredential();
  }
}
